import React, { useState } from 'react';
import { Wrench, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import type { AgentToolStep } from '../services/agentApi';

/**
 * Collapsible "tools used" strip shown above assistant bubbles in
 * `AgentChatPage`. Mirrors the V2 LangGraph ReAct agent's tool-call
 * trace: each ``tool_step`` SSE event from
 * `janus-tentant-agent-ai/app/routers/agent.py` becomes one row here.
 *
 * Collapsed by default — the chat is already dense with the prose reply
 * and any Janus entity cards, so we only want the tool names visible at
 * a glance ("3 ferramentas usadas"). Expanding reveals each step's
 * arguments and a short preview of the tool output.
 *
 * The panel is intentionally narrow and muted. It's observability,
 * not primary content: users should be able to ignore it completely
 * unless they want to debug "why did the agent answer X?".
 */
interface ToolStepsPanelProps {
  steps: AgentToolStep[];
}

const ToolStepsPanel: React.FC<ToolStepsPanelProps> = ({ steps }) => {
  const [expanded, setExpanded] = useState(false);
  if (steps.length === 0) return null;

  const label = steps.length === 1 ? '1 ferramenta usada' : `${steps.length} ferramentas usadas`;

  return (
    <div
      style={{
        marginBottom: 6,
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        background: 'var(--color-surface-muted)',
        fontSize: 12,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          padding: '6px 10px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
        }}
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Wrench size={12} color="var(--color-brand)" />
        <span>{label}</span>
      </button>
      {expanded && (
        <div style={{ padding: '4px 10px 8px 24px' }}>
          {steps.map((step, idx) => (
            <div
              key={step.id ?? idx}
              style={{
                borderLeft: '2px solid var(--color-border)',
                paddingLeft: 8,
                marginBottom: idx === steps.length - 1 ? 0 : 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {step.ok === false ? (
                  <XCircle size={11} color="var(--color-error)" />
                ) : (
                  <CheckCircle2 size={11} color="var(--color-success)" />
                )}
                <code
                  style={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 11,
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {step.name}
                </code>
              </div>
              {step.args && Object.keys(step.args).length > 0 && (
                <div
                  style={{
                    marginTop: 2,
                    marginLeft: 15,
                    color: 'var(--color-text-secondary)',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 11,
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(step.args)}
                </div>
              )}
              {step.summary && (
                <div
                  style={{
                    marginTop: 2,
                    marginLeft: 15,
                    color: 'var(--color-text-tertiary)',
                    fontSize: 11,
                    fontStyle: 'italic',
                    maxHeight: 40,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  → {step.summary}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolStepsPanel;
